// staleRendering.js — ticket 04: shared "stale / last-known" OL style treatment for the DTG
// cursor feature. Used by both markingFeatureStyle (regular markings/unit-icons) and
// tacticalFeatureStyle (multipoint tactical graphics) in renderer/markingStyle.js — the single
// shared styling implementation both the live editor (useMapEditor.js) and the report-embed
// preview (MapPreviewEngine.js) consume — so a moving track whose resolved
// snapshot (see resolveTrackAtDtg in schema/mapPmAdapter.js) is stale relative to the DTG cursor
// renders identically — reduced opacity + a small "LAST KNOWN" text badge — across both style
// pipelines instead of two independently-tuned implementations drifting apart.

import {Fill, Stroke, Text as OLText} from 'ol/style';

// Rendering rule (ticket 04): moving + not-current → ~40-50% opacity + badge.
export const STALE_OPACITY = 0.45;
export const STALE_BADGE_TEXT = 'LAST KNOWN';
const BADGE_FILL_COLOR = '#7a1f1f';
const BADGE_STROKE_COLOR = '#ffffff';
const DEFAULT_BADGE_OFFSET_Y = 16;

/**
 * Multiply a CSS color string's alpha channel by `factor`. Handles `#rgb`/`#rrggbb`,
 * `rgb(...)`/`rgba(...)`; anything else (e.g. a named color) is returned unchanged — in this
 * codebase, colors reaching Fill/Stroke are always hex or the `rgba(...)` output of
 * hexWithOpacity(), and Icon/CircleStyle opacity is faded separately via ImageStyle#setOpacity
 * (see applyStaleRendering below), so a color-string fade isn't load-bearing there.
 */
export function fadeCssColor(color, factor) {
    if (!color) return color;
    const str = String(color).trim();
    const rgbaMatch = /^rgba?\(([^)]+)\)$/i.exec(str);
    if (rgbaMatch) {
        const parts = rgbaMatch[1].split(',').map((s) => s.trim());
        const [r, g, b, a] = parts;
        const alpha = a !== undefined ? parseFloat(a) : 1;
        return `rgba(${r},${g},${b},${alpha * factor})`;
    }
    if (str.startsWith('#')) {
        let hex = str.slice(1);
        if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
        if (hex.length === 6) {
            const r = parseInt(hex.slice(0, 2), 16);
            const g = parseInt(hex.slice(2, 4), 16);
            const b = parseInt(hex.slice(4, 6), 16);
            return `rgba(${r},${g},${b},${factor})`;
        }
    }
    return color;
}

/** Build (or extend) the "LAST KNOWN" text badge, preserving an existing modifier/label text
 *  (e.g. a tactical graphic's AM modifier text) by appending the badge on a second line rather
 *  than clobbering it. */
function withBadge(existingText, offsetY) {
    if (existingText) {
        const clone = existingText.clone();
        clone.setText(`${existingText.getText()}\n${STALE_BADGE_TEXT}`);
        if (existingText.getOffsetY() == null) clone.setOffsetY(offsetY);
        return clone;
    }
    return new OLText({
        text: STALE_BADGE_TEXT,
        font: 'bold 10px sans-serif',
        fill: new Fill({color: BADGE_FILL_COLOR}),
        stroke: new Stroke({color: BADGE_STROKE_COLOR, width: 2}),
        offsetY,
    });
}

/**
 * Apply the "stale / last known" treatment (ticket 04) to an existing OL Style (or array of
 * Styles): fades image/fill/stroke opacity to STALE_OPACITY and attaches a small "LAST KNOWN"
 * text badge near the symbol. Returns new Style instance(s) — never mutates the input, since
 * callers may share cached Style objects (e.g. useMapEditor's symbolStyleForSidc cache) across
 * features that aren't all stale.
 *
 * @param {import('ol/style').Style|Array<import('ol/style').Style>|null} style
 * @param {{offsetY?: number}} [opts]
 */
export function applyStaleRendering(style, opts = {}) {
    if (!style) return style;
    const offsetY = opts.offsetY ?? DEFAULT_BADGE_OFFSET_Y;
    const styles = Array.isArray(style) ? style : [style];
    const faded = styles.map((s) => {
        const clone = s.clone();
        const image = clone.getImage();
        if (image) image.setOpacity(image.getOpacity() * STALE_OPACITY);
        const fill = clone.getFill();
        if (fill) {
            const f = fill.clone();
            f.setColor(fadeCssColor(f.getColor(), STALE_OPACITY));
            clone.setFill(f);
        }
        const stroke = clone.getStroke();
        if (stroke) {
            const st = stroke.clone();
            st.setColor(fadeCssColor(st.getColor(), STALE_OPACITY));
            clone.setStroke(st);
        }
        return clone;
    });
    // Attach the badge once, on the last style in the array — avoids stacking duplicate labels
    // when a feature resolves to multiple Style layers (e.g. polygon fill+stroke).
    const last = faded[faded.length - 1];
    if (last) last.setText(withBadge(last.getText(), offsetY));
    return Array.isArray(style) ? faded : faded[0];
}

/**
 * ticket 05: movement trail — fade an existing OL Style (or array of Styles) to
 * `opacityFraction` WITHOUT attaching the "LAST KNOWN" badge that `applyStaleRendering` adds.
 * Trail ghosts are an intentional history view (the user opted into "Show movement trail"), not
 * a stale-data warning, so the two are kept as small parallel helpers sharing the same
 * image/fill/stroke fade mechanics rather than teaching `applyStaleRendering` an
 * "opts.badge: false" branch — this keeps that already-well-tested function's contract
 * (always fade + always badge) unchanged for every existing caller.
 *
 * Returns new Style instance(s) — never mutates the input, same rationale as
 * `applyStaleRendering` (callers may share cached Style objects, e.g. `symbolStyleForSidc`'s
 * cache in useMapEditor.js, across features that aren't all trail ghosts).
 *
 * @param {import('ol/style').Style|Array<import('ol/style').Style>|null} style
 * @param {number} opacityFraction - 0..1 fade factor (ticket 05: `getTrailSnapshots`' per-
 *   snapshot `opacity` field, `TRAIL_MIN_OPACITY`..`TRAIL_MAX_OPACITY`).
 */
export function applyTrailOpacity(style, opacityFraction) {
    if (!style) return style;
    const factor = Math.min(Math.max(opacityFraction ?? 1, 0), 1);
    const styles = Array.isArray(style) ? style : [style];
    const faded = styles.map((s) => {
        const clone = s.clone();
        const image = clone.getImage();
        if (image) image.setOpacity(image.getOpacity() * factor);
        const fill = clone.getFill();
        if (fill) {
            const f = fill.clone();
            f.setColor(fadeCssColor(f.getColor(), factor));
            clone.setFill(f);
        }
        const stroke = clone.getStroke();
        if (stroke) {
            const st = stroke.clone();
            st.setColor(fadeCssColor(st.getColor(), factor));
            clone.setStroke(st);
        }
        // Trail ghosts intentionally drop any text (e.g. a unit's label/modifier) — a faded
        // duplicate label next to every ghost symbol would clutter the trail rather than read
        // as a clean fading path.
        clone.setText(null);
        return clone;
    });
    return Array.isArray(style) ? faded : faded[0];
}
